import React, { useState } from 'react';
import styles from './AuthForm.module.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { showSuccess, showError } from '../../../../components/ModernSnackbar';
import { resetPassword } from '../../../../services/api/fytnServices';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

// Password validation schema (same as Login.js)
const passwordSchema = yup
  .string()
  .required('Password is required')
  .min(8, 'Minimum 8 characters')
  .matches(/[A-Z]/, 'Must include one uppercase letter')
  .matches(/[a-z]/, 'Must include one lowercase letter')
  .matches(/[0-9]/, 'Must include one number')
  .matches(/[@$!%*#?&]/, 'Must include one special character');

const resetSchema = yup.object().shape({
  password: passwordSchema,
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Please confirm your password'),
});

const ResetPassword = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  // Redirect if no token is present
  React.useEffect(() => {
    if (!token) {
      navigate(-1); // Go back to previous page
    }
  }, [token, navigate]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(resetSchema),
    mode: 'onChange', // Enable validation on change
    reValidateMode: 'onChange',
  });

  const onSubmit = async (data) => {
    console.log('Form submitted with data:', data);
    setLoading(true);
    try {
      await resetPassword(token, data.password);
              showSuccess('Password reset successful! Please login.');
      reset();
      navigate('/login');
    } catch (error) {
              showError('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Debug: Log errors when they change
  React.useEffect(() => {
    console.log('Form errors:', errors);
  }, [errors]);

  return (
    <div className={styles.authContainer}>
      <div className={styles.formContainer}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <h1>Reset Password</h1>
          <div className={styles.passwordWrapper}>
            <input
              type={showPassword ? 'text' : 'password'}
              {...register('password')}
              className={styles.input}
              placeholder="Enter new password"
              autoComplete="new-password"
            />
            <span
              onClick={() => setShowPassword((prev) => !prev)}
              className={styles.eyeIcon}
              tabIndex={0}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
          {errors.password && (
            <p style={{ color: 'red', fontSize: '14px', margin: '5px 0' }}>
              {errors.password.message}
            </p>
          )}
          <div className={styles.passwordWrapper}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              {...register('confirmPassword')}
              className={styles.input}
              placeholder="Confirm new password"
              autoComplete="new-password"
            />
            <span
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              className={styles.eyeIcon}
              tabIndex={0}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
          {errors.confirmPassword && (
            <p style={{ color: 'red', fontSize: '14px', margin: '5px 0' }}>
              {errors.confirmPassword.message}
            </p>
          )}
          <button
            type="submit"
            className={styles.btn}
            disabled={loading || !isValid}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
